import { CompanyTrainingNeed } from "../../types";

/*
 La risposta di `PUT /company-training-needs`, cioè la tabella del fabbisogno dopo il salvataggio.

 Prima il backend rispondeva con un array di entità che il frontend ignorava: dopo aver salvato,
 i campi a schermo ripartivano dallo stato caricato all'apertura della pagina e mostravano ancora
 il numero di prima (BUG-25). Ora la risposta è la tabella **intera** dell'azienda, non solo le
 righe toccate, con SVOLTI e DA FARE già ricalcolati: basta metterla al posto dello stato, senza
 una seconda chiamata e senza ricaricare.

 `ricalcoloCorsiMancantiInCorso` è l'unica cosa in più rispetto alla GET: i numeri qui dentro sono
 definitivi, ma l'elenco dei *corsi mancanti* — che si vede altrove, nelle pagine degli attestati —
 viene rigenerato in sottofondo e può essere indietro di qualche istante.
*/
export type RispostaSalvataggioFabbisogno = {
  fabbisogni: RigaSalvata[];
  ricalcoloCorsiMancantiInCorso: boolean;
};

type RigaSalvata = {
  id: number;
  courseTitle: string | null;
  associatedCourseTitle: string | null;
  courseCode: string | null;
  trainingNeed: number;
  active: number;
  pending: number;
  subscribed: boolean;
};

function eUnaRiga(valore: unknown): valore is RigaSalvata {
  const riga = valore as RigaSalvata;
  return (
    !!riga &&
    typeof riga === "object" &&
    typeof riga.id === "number" &&
    typeof riga.trainingNeed === "number" &&
    typeof riga.active === "number" &&
    typeof riga.pending === "number"
  );
}

/*
 La tabella da mettere a schermo, letta dalla risposta del salvataggio.

 Restituisce `null` — e non una tabella vuota — quando la risposta non ha la forma nuova: un
 backend più vecchio rispondeva con l'array nudo, e svuotare la tabella sarebbe peggio del difetto
 che si sta correggendo. Chi chiama, davanti a `null`, rilegge con la GET.

 `company` non viaggia nella risposta e non serve a questa pagina: la riga la dichiara `null`
 perché il tipo condiviso con la GET la prevede.
*/
export function tabellaDalSalvataggio(
  dati: unknown
): CompanyTrainingNeed[] | null {
  const risposta = dati as RispostaSalvataggioFabbisogno;
  if (!risposta || typeof risposta !== "object") return null;
  if (!Array.isArray(risposta.fabbisogni)) return null;
  if (!risposta.fabbisogni.every(eUnaRiga)) return null;

  return risposta.fabbisogni.map((riga) => ({
    id: riga.id,
    company: null,
    courseTitle: riga.courseTitle ?? "",
    associatedCourseTitle: riga.associatedCourseTitle ?? "",
    courseCode: riga.courseCode ?? "",
    trainingNeed: riga.trainingNeed,
    active: riga.active,
    pending: riga.pending,
    subscribed: riga.subscribed === true,
  }));
}

/*
 Se i corsi mancanti si stanno ancora rigenerando in sottofondo. Assente o non booleano vale
 «no»: è l'ipotesi che non aggiunge nulla a schermo.
*/
export function ricalcoloCorsiMancantiInCorso(dati: unknown): boolean {
  return (dati as RispostaSalvataggioFabbisogno)
    ?.ricalcoloCorsiMancantiInCorso === true;
}

/* I valori di partenza dei campi, indicizzati per riga, come li dà la tabella. */
export function valoriDeiCampi(righe: CompanyTrainingNeed[]): {
  [key: number]: number;
} {
  return righe.reduce((acc, riga) => ({ ...acc, [riga.id]: riga.trainingNeed }), {});
}
